import cn from 'classnames';
import { memo, MouseEvent, ReactNode, SyntheticEvent, useCallback } from 'react';
import {
  MdIosShare,
  MdOutlineChatBubbleOutline,
  MdOutlineGrade,
  MdOutlinePushPin
} from 'react-icons/md';
import { ActivityStateAction, Event } from '@blisspointmedia/bpm-types/dist/OracleActivity';
import { Badge } from '@Components/Badge';
import { Checkbox } from '@Components/FormCheck';
import { IconButton } from '@Components/IconButton';
import { InteractiveDiv } from '@Components/InteractiveEl';
import { Appearance, Design, Size } from '@Components/designConstants';
import { GHOST_ICON_BUTTON_PROPS } from '../activity-feed-constants';
import { ActivityId } from '../activity-feed-data-utils';
import {
  formatRelativeActivityDate,
  getProvenanceDisplay,
  nextPinAction,
  nextStarAction,
  useNotImplemented,
  useShowWipActions
} from '../activity-feed-utils';
import { ActivityFeedClientBadge } from '../ActivityFeedClientBadge';
import { ImportanceBars } from './ImportanceBars';

// Signature matches the return of useToggleActivityState. Kept as an exported alias so the
// parent that owns the hoisted toggle can type the prop without duplicating the tuple.
export type EventCardToggleHandler = (
  e: MouseEvent,
  activityId: ActivityId,
  action: ActivityStateAction
) => Promise<unknown>;

interface EventCardProps {
  event: Event;
  isSelected: boolean;
  isChecked: boolean;
  onToggleCheckedId: (id: ActivityId) => void;
  // Takes an id so the parent can hand down a single stable handler across the whole list
  // instead of minting a per-card closure — otherwise memo() would be defeated on every render.
  onSelectId: (id: ActivityId) => void;
  // Hoisted at the list-body level (see ActivityFeedList) so a single mutation observer serves
  // every card and cards don't each subscribe to ActivityFeed context.
  onToggleState: EventCardToggleHandler;
}

// The card is an InteractiveDiv whose Enter/Space handler would otherwise select the card
// and preventDefault the checkbox toggle.
const stopPropagation = (e: SyntheticEvent) => e.stopPropagation();

// memo + stable onSelectId + stable onToggleState means only the cards whose event / isSelected
// props actually flip re-render, not every card in the list on every selection or mutation.
export const EventCard = memo(
  ({
    event,
    isSelected,
    isChecked,
    onToggleCheckedId,
    onSelectId,
    onToggleState
  }: EventCardProps): ReactNode => {
    const {
      id: activityId,
      cid,
      origin,
      importance,
      signalTag,
      title,
      details,
      date,
      commentCount,
      isStarred = false,
      isPinned = false
    } = event;
    const onSelect = useCallback(() => onSelectId(activityId), [onSelectId, activityId]);
    const onToggleChecked = useCallback(
      () => onToggleCheckedId(activityId),
      [onToggleCheckedId, activityId]
    );
    const notImplemented = useNotImplemented();
    const showWip = useShowWipActions();
    const provenance = getProvenanceDisplay(origin);
    const { icon: ProvenanceIcon } = provenance;

    const handleShare = useCallback(
      (e: MouseEvent) => {
        e.stopPropagation();
        notImplemented();
      },
      [notImplemented]
    );

    const handleStar = (e: MouseEvent) => onToggleState(e, activityId, nextStarAction(isStarred));
    const handlePin = (e: MouseEvent) => onToggleState(e, activityId, nextPinAction(isPinned));

    return (
      <InteractiveDiv
        className={cn('activity-feed-card', { selected: isSelected })}
        onClick={onSelect}
      >
        <div className='activity-feed-card-rail'>
          <Checkbox
            aria-label={`Select ${title}`}
            checked={isChecked}
            onChange={onToggleChecked}
            onClick={stopPropagation}
            onKeyDown={stopPropagation}
          />
          <ImportanceBars importance={importance} signalTag={signalTag} />
        </div>
        <div className='activity-feed-card-body'>
          <div className='activity-feed-card-content'>
            <p className='activity-feed-card-title'>{title}</p>
            <p className='activity-feed-card-description'>{details}</p>
            <div className='activity-feed-card-meta-row'>
              <Badge
                size={Size.SM}
                design={Design.NEUTRAL}
                appearance={Appearance.EMPTY}
                leadingElement={<ProvenanceIcon />}
              >
                {provenance.label}
              </Badge>
              <ActivityFeedClientBadge cid={cid} size={Size.SM} appearance={Appearance.EMPTY} />
              {commentCount > 0 && (
                <Badge
                  size={Size.SM}
                  design={Design.NEUTRAL}
                  appearance={Appearance.EMPTY}
                  leadingElement={<MdOutlineChatBubbleOutline />}
                >
                  {commentCount}
                </Badge>
              )}
            </div>
          </div>
          <div className='activity-feed-card-side'>
            {showWip && (
              <div className='activity-feed-card-actions'>
                <IconButton {...GHOST_ICON_BUTTON_PROPS} aria-label='Share' onClick={handleShare}>
                  <MdIosShare />
                </IconButton>
                <IconButton {...GHOST_ICON_BUTTON_PROPS} aria-label='Star' onClick={handleStar}>
                  <MdOutlineGrade />
                </IconButton>
                <IconButton {...GHOST_ICON_BUTTON_PROPS} aria-label='Pin' onClick={handlePin}>
                  <MdOutlinePushPin />
                </IconButton>
              </div>
            )}
            <span className='activity-feed-card-timestamp'>{formatRelativeActivityDate(date)}</span>
          </div>
        </div>
      </InteractiveDiv>
    );
  }
);
EventCard.displayName = 'EventCard';
